import { Capacitor } from '@capacitor/core'
import maplibregl from 'maplibre-gl'
import { booleanPointInPolygon, point as turfPoint } from '@turf/turf'
import { logger } from '../../utils/logger.js'
import { addOverlayLayer } from '../../components/map/layer-order-manager.js'
import type { FireDataType } from './fire-types.js'
import { ALL_FIRE_TYPES } from './fire-types.js'

const log = logger.create('fire-layers')

const DATA_ORIGIN = Capacitor.isNativePlatform() ? 'https://taselith.com' : ''
const BASE_URL = `${DATA_ORIGIN}/data/fire`
const CACHE_TTL_MS = 60 * 60 * 1000

const EMPTY_GEOJSON: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] }

export type WfigsIncidentType = 'WF' | 'RX' | 'CX'
export type FirmsSensor = 'VIIRS NOAA-21' | 'Landsat'
export type FirmsConfidence = 'low' | 'nominal' | 'high'
export type RestrictionSource = 'nps' | 'usfws'

export const ALL_WFIGS_INCIDENT_TYPES: WfigsIncidentType[] = ['WF', 'RX', 'CX']
export const ALL_FIRMS_SENSORS: FirmsSensor[] = ['VIIRS NOAA-21', 'Landsat']
export const ALL_FIRMS_CONFIDENCE: FirmsConfidence[] = ['low', 'nominal', 'high']
export const ALL_RESTRICTION_SOURCES: RestrictionSource[] = ['nps', 'usfws']

const RESTRICTION_SOURCE_MAP: Record<RestrictionSource, FireDataType> = {
  nps: 'nps-restrictions',
  usfws: 'usfws-restrictions',
}

const CONFIDENCE_RAW_MAP: Record<string, FirmsConfidence> = {
  l: 'low',
  L: 'low',
  n: 'nominal',
  m: 'nominal',
  M: 'nominal',
  h: 'high',
  H: 'high',
}

export function firmsConfidenceLabel(raw: string): string {
  return CONFIDENCE_RAW_MAP[raw] ?? raw
}

function rawConfidenceValues(levels: ReadonlySet<FirmsConfidence>): string[] {
  const vals: string[] = []
  if (levels.has('low')) vals.push('l', 'L')
  if (levels.has('nominal')) vals.push('n', 'm', 'M')
  if (levels.has('high')) vals.push('h', 'H')
  return vals
}

const POLYGON_TYPES: ReadonlySet<FireDataType> = new Set<FireDataType>(['usfws-restrictions'])

function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371
  const dLat = ((lat2 - lat1) * Math.PI) / 180
  const dLon = ((lon2 - lon1) * Math.PI) / 180
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

function featureCentroid(feature: GeoJSON.Feature): [number, number] | null {
  const geom = feature.geometry
  if (geom.type === 'Point') return geom.coordinates as [number, number]
  if (geom.type === 'MultiPoint' && geom.coordinates.length > 0)
    return geom.coordinates[0] as [number, number]
  return null
}

export interface FireFeatureHit {
  fireType: FireDataType
  feature: GeoJSON.Feature
  properties: Record<string, unknown>
  distanceKm: number | null
}

interface CachedData {
  data: GeoJSON.FeatureCollection
  fetchedAt: number
}

interface LayerSpec {
  id: string
  spec: maplibregl.LayerSpecification
}

interface GeoJSONLayerDef {
  kind: 'geojson'
  type: FireDataType
  sourceId: string
  file: string
  layers: LayerSpec[]
}

interface PMTilesLayerDef {
  kind: 'pmtiles'
  type: FireDataType
  sourceId: string
  pmtilesFile: string
  sourceLayer: string
  layers: LayerSpec[]
}

type FireLayerDef = GeoJSONLayerDef | PMTilesLayerDef

function srcId(t: FireDataType): string {
  return `fire-${t}`
}

const LAYER_DEFS: FireLayerDef[] = [
  {
    kind: 'geojson',
    type: 'wfigs-fires',
    sourceId: srcId('wfigs-fires'),
    file: 'wfigs-fires.geojson',
    layers: [
      {
        id: 'fire-wfigs-fill',
        spec: {
          id: 'fire-wfigs-fill',
          type: 'fill',
          source: srcId('wfigs-fires'),
          filter: ['==', ['geometry-type'], 'Polygon'],
          paint: { 'fill-color': '#dc2626', 'fill-opacity': 0.25 },
        },
      },
      {
        id: 'fire-wfigs-line',
        spec: {
          id: 'fire-wfigs-line',
          type: 'line',
          source: srcId('wfigs-fires'),
          filter: ['==', ['geometry-type'], 'Polygon'],
          paint: { 'line-color': '#991b1b', 'line-width': 2 },
        },
      },
      {
        id: 'fire-wfigs-circle',
        spec: {
          id: 'fire-wfigs-circle',
          type: 'circle',
          source: srcId('wfigs-fires'),
          filter: ['==', ['geometry-type'], 'Point'],
          paint: {
            'circle-radius': [
              'interpolate',
              ['linear'],
              ['coalesce', ['get', 'acres'], 0],
              0,
              4,
              100,
              6,
              1000,
              9,
              10000,
              13,
              100000,
              18,
            ],
            'circle-color': [
              'interpolate',
              ['linear'],
              ['coalesce', ['get', 'percentContained'], 0],
              0,
              '#dc2626',
              50,
              '#f59e0b',
              100,
              '#16a34a',
            ],
            'circle-stroke-color': '#ffffff',
            'circle-stroke-width': 1.5,
            'circle-opacity': 0.85,
          },
        },
      },
      {
        id: 'fire-wfigs-perim-label',
        spec: {
          id: 'fire-wfigs-perim-label',
          type: 'symbol',
          source: srcId('wfigs-fires'),
          filter: ['==', ['geometry-type'], 'Polygon'],
          minzoom: 8,
          layout: { 'text-field': ['get', 'name'], 'text-size': 12, 'text-allow-overlap': false },
          paint: { 'text-color': '#991b1b', 'text-halo-color': '#ffffff', 'text-halo-width': 1.5 },
        },
      },
      {
        id: 'fire-wfigs-point-label',
        spec: {
          id: 'fire-wfigs-point-label',
          type: 'symbol',
          source: srcId('wfigs-fires'),
          filter: ['==', ['geometry-type'], 'Point'],
          minzoom: 7,
          layout: {
            'text-field': ['get', 'name'],
            'text-size': 11,
            'text-offset': [0, 1.5],
            'text-allow-overlap': false,
          },
          paint: { 'text-color': '#991b1b', 'text-halo-color': '#ffffff', 'text-halo-width': 1.5 },
        },
      },
    ],
  },
  {
    kind: 'geojson',
    type: 'firms-hotspots',
    sourceId: srcId('firms-hotspots'),
    file: 'firms-hotspots.geojson',
    layers: [
      {
        id: 'fire-firms-circle',
        spec: {
          id: 'fire-firms-circle',
          type: 'circle',
          source: srcId('firms-hotspots'),
          paint: {
            'circle-radius': [
              'interpolate',
              ['linear'],
              ['coalesce', ['get', 'frp'], 0],
              0,
              3,
              10,
              5,
              50,
              8,
              200,
              12,
            ],
            'circle-color': [
              'interpolate',
              ['linear'],
              ['coalesce', ['get', 'frp'], 0],
              0,
              '#fbbf24',
              25,
              '#f97316',
              100,
              '#dc2626',
            ],
            'circle-stroke-color': '#ffffff',
            'circle-stroke-width': 0.5,
            'circle-opacity': 0.8,
          },
        },
      },
    ],
  },
  {
    kind: 'pmtiles',
    type: 'nps-restrictions',
    sourceId: srcId('nps-restrictions'),
    pmtilesFile: 'nps-fire-restrictions.pmtiles',
    sourceLayer: 'nps_fire_restrictions',
    layers: [
      {
        id: 'fire-nps-fill',
        spec: {
          id: 'fire-nps-fill',
          type: 'fill',
          source: srcId('nps-restrictions'),
          'source-layer': 'nps_fire_restrictions',
          paint: { 'fill-color': '#ea580c', 'fill-opacity': 0.2 },
        } as maplibregl.LayerSpecification,
      },
      {
        id: 'fire-nps-line',
        spec: {
          id: 'fire-nps-line',
          type: 'line',
          source: srcId('nps-restrictions'),
          'source-layer': 'nps_fire_restrictions',
          paint: { 'line-color': '#ea580c', 'line-width': 2 },
        } as maplibregl.LayerSpecification,
      },
    ],
  },
  {
    kind: 'geojson',
    type: 'usfws-restrictions',
    sourceId: srcId('usfws-restrictions'),
    file: 'usfws-restrictions.geojson',
    layers: [
      {
        id: 'fire-usfws-fill',
        spec: {
          id: 'fire-usfws-fill',
          type: 'fill',
          source: srcId('usfws-restrictions'),
          paint: { 'fill-color': '#d97706', 'fill-opacity': 0.2 },
        },
      },
      {
        id: 'fire-usfws-line',
        spec: {
          id: 'fire-usfws-line',
          type: 'line',
          source: srcId('usfws-restrictions'),
          paint: { 'line-color': '#d97706', 'line-width': 2 },
        },
      },
    ],
  },
]

class FireLayerService {
  private map: maplibregl.Map | null = null
  private enabledTypes = new Set<FireDataType>()
  private dataCache = new Map<FireDataType, CachedData>()
  private opacity = 1.0
  private _wfigsIncidentTypes = new Set<WfigsIncidentType>(ALL_WFIGS_INCIDENT_TYPES)
  private _firmsSensors = new Set<FirmsSensor>(ALL_FIRMS_SENSORS)
  private _firmsConfidence = new Set<FirmsConfidence>(['nominal', 'high'])
  private _restrictionSources = new Set<RestrictionSource>(ALL_RESTRICTION_SOURCES)

  get wfigsIncidentTypes(): ReadonlySet<WfigsIncidentType> {
    return this._wfigsIncidentTypes
  }

  get firmsSensors(): ReadonlySet<FirmsSensor> {
    return this._firmsSensors
  }

  get firmsConfidence(): ReadonlySet<FirmsConfidence> {
    return this._firmsConfidence
  }

  get restrictionSources(): ReadonlySet<RestrictionSource> {
    return this._restrictionSources
  }

  setWfigsIncidentTypes(types: WfigsIncidentType[]): void {
    this._wfigsIncidentTypes = new Set(types.length > 0 ? types : ALL_WFIGS_INCIDENT_TYPES)
    this.applyWfigsFilter()
  }

  setFirmsSensors(sensors: FirmsSensor[]): void {
    this._firmsSensors = new Set(sensors.length > 0 ? sensors : ALL_FIRMS_SENSORS)
    this.applyFirmsFilter()
  }

  setFirmsConfidence(levels: FirmsConfidence[]): void {
    this._firmsConfidence = new Set(levels.length > 0 ? levels : ALL_FIRMS_CONFIDENCE)
    this.applyFirmsFilter()
  }

  setRestrictionSources(sources: RestrictionSource[]): void {
    this._restrictionSources = new Set(sources.length > 0 ? sources : ALL_RESTRICTION_SOURCES)
    for (const src of ALL_RESTRICTION_SOURCES) {
      const dt = RESTRICTION_SOURCE_MAP[src]
      if (this._restrictionSources.has(src)) {
        if (this.isRestrictionsEnabled()) this.enable(dt)
      } else {
        this.disable(dt)
      }
    }
  }

  isRestrictionsEnabled(): boolean {
    return this.enabledTypes.has('nps-restrictions') || this.enabledTypes.has('usfws-restrictions')
  }

  toggleRestrictions(): void {
    if (this.isRestrictionsEnabled()) {
      this.disable('nps-restrictions')
      this.disable('usfws-restrictions')
    } else {
      for (const src of this._restrictionSources) {
        this.enable(RESTRICTION_SOURCE_MAP[src])
      }
    }
  }

  enableRestrictions(): void {
    for (const src of this._restrictionSources) {
      this.enable(RESTRICTION_SOURCE_MAP[src])
    }
  }

  private applyWfigsFilter(): void {
    if (!this.map || !this.enabledTypes.has('wfigs-fires')) return
    const typeValues = [...this._wfigsIncidentTypes]
    const typeFilter: maplibregl.FilterSpecification = [
      'in',
      ['get', 'incidentType'],
      ['literal', typeValues],
    ]
    const wfigsLayers: Array<{ id: string; geomFilter: maplibregl.FilterSpecification }> = [
      { id: 'fire-wfigs-fill', geomFilter: ['==', ['geometry-type'], 'Polygon'] },
      { id: 'fire-wfigs-line', geomFilter: ['==', ['geometry-type'], 'Polygon'] },
      { id: 'fire-wfigs-circle', geomFilter: ['==', ['geometry-type'], 'Point'] },
      { id: 'fire-wfigs-perim-label', geomFilter: ['==', ['geometry-type'], 'Polygon'] },
      { id: 'fire-wfigs-point-label', geomFilter: ['==', ['geometry-type'], 'Point'] },
    ]
    for (const { id, geomFilter } of wfigsLayers) {
      if (!this.map.getLayer(id)) continue
      this.map.setFilter(id, ['all', geomFilter, typeFilter] as maplibregl.FilterSpecification)
    }
  }

  private applyFirmsFilter(): void {
    if (!this.map || !this.enabledTypes.has('firms-hotspots')) return
    const layerId = 'fire-firms-circle'
    if (!this.map.getLayer(layerId)) return

    const sensorValues = [...this._firmsSensors]
    const confValues = rawConfidenceValues(this._firmsConfidence)

    const filter: maplibregl.FilterSpecification = [
      'all',
      ['in', ['get', 'sensor'], ['literal', sensorValues]],
      ['in', ['get', 'confidence'], ['literal', confValues]],
    ]
    this.map.setFilter(layerId, filter)
  }

  register(map: maplibregl.Map): void {
    this.map = map
  }

  unregister(): void {
    for (const t of ALL_FIRE_TYPES) this.disable(t)
    this.map = null
  }

  isEnabled(t: FireDataType): boolean {
    return this.enabledTypes.has(t)
  }

  anyEnabled(): boolean {
    return this.enabledTypes.size > 0
  }

  enable(t: FireDataType): void {
    if (!this.map || this.enabledTypes.has(t)) return
    this.enabledTypes.add(t)
    const def = LAYER_DEFS.find(d => d.type === t)!
    this.addLayers(def)
    if (t === 'wfigs-fires') this.applyWfigsFilter()
    if (t === 'firms-hotspots') this.applyFirmsFilter()
    if (def.kind === 'geojson') void this.loadData(def)
  }

  disable(t: FireDataType): void {
    if (!this.map) return
    this.enabledTypes.delete(t)
    const def = LAYER_DEFS.find(d => d.type === t)!
    this.removeLayers(def)
  }

  toggle(t: FireDataType): void {
    if (this.enabledTypes.has(t)) this.disable(t)
    else this.enable(t)
  }

  enableAll(): void {
    for (const t of ALL_FIRE_TYPES) this.enable(t)
  }

  disableAll(): void {
    for (const t of ALL_FIRE_TYPES) this.disable(t)
  }

  reAdd(): void {
    if (!this.map) return
    for (const def of LAYER_DEFS) {
      if (this.enabledTypes.has(def.type) && !this.map.getSource(def.sourceId)) {
        this.addLayers(def)
        if (def.kind === 'geojson') void this.loadData(def)
      }
    }
    if (this.opacity !== 1.0) this.setOpacity(this.opacity)
    if (this.enabledTypes.has('wfigs-fires')) this.applyWfigsFilter()
    if (this.enabledTypes.has('firms-hotspots')) this.applyFirmsFilter()
  }

  setOpacity(opacity: number): void {
    this.opacity = opacity
    if (!this.map) return
    for (const def of LAYER_DEFS) {
      if (!this.enabledTypes.has(def.type)) continue
      for (const layer of def.layers) {
        if (!this.map.getLayer(layer.id)) continue
        const type = layer.spec.type
        if (type === 'fill') this.map.setPaintProperty(layer.id, 'fill-opacity', opacity * 0.25)
        else if (type === 'line') this.map.setPaintProperty(layer.id, 'line-opacity', opacity)
        else if (type === 'circle')
          this.map.setPaintProperty(layer.id, 'circle-opacity', opacity * 0.85)
        else if (type === 'symbol') this.map.setPaintProperty(layer.id, 'text-opacity', opacity)
      }
    }
  }

  getSelectableLayerIds(): string[] {
    const ids: string[] = []
    for (const def of LAYER_DEFS) {
      if (!this.enabledTypes.has(def.type)) continue
      for (const layer of def.layers) {
        if (layer.spec.type === 'fill' || layer.spec.type === 'circle') {
          ids.push(layer.id)
        }
      }
    }
    return ids
  }

  queryFeatures(point: maplibregl.Point): maplibregl.MapGeoJSONFeature[] {
    if (!this.map) return []
    const layerIds = this.getSelectableLayerIds().filter(id => this.map!.getLayer(id))
    if (layerIds.length === 0) return []
    return this.map.queryRenderedFeatures(point, { layers: layerIds })
  }

  typeForLayerId(layerId: string): FireDataType | undefined {
    for (const def of LAYER_DEFS) {
      if (def.layers.some(l => l.id === layerId)) return def.type
    }
    return undefined
  }

  private addLayers(def: FireLayerDef): void {
    if (!this.map) return
    if (!this.map.getSource(def.sourceId)) {
      if (def.kind === 'pmtiles') {
        this.map.addSource(def.sourceId, {
          type: 'vector',
          url: `pmtiles://${BASE_URL}/${def.pmtilesFile}`,
          minzoom: 3,
          maxzoom: 12,
        })
      } else {
        this.map.addSource(def.sourceId, { type: 'geojson', data: EMPTY_GEOJSON })
      }
    }
    for (const layer of def.layers) {
      if (!this.map.getLayer(layer.id)) {
        addOverlayLayer(this.map, layer.spec, `fire-${def.type}`)
      }
    }
  }

  private removeLayers(def: FireLayerDef): void {
    if (!this.map) return
    for (const layer of [...def.layers].reverse()) {
      if (this.map.getLayer(layer.id)) this.map.removeLayer(layer.id)
    }
    if (this.map.getSource(def.sourceId)) this.map.removeSource(def.sourceId)
  }

  async queryNearPoint(lat: number, lng: number, radiusKm = 10): Promise<FireFeatureHit[]> {
    const geojsonDefs = LAYER_DEFS.filter((d): d is GeoJSONLayerDef => d.kind === 'geojson')
    await Promise.all(geojsonDefs.map(def => this.ensureDataLoaded(def)))

    const pt = turfPoint([lng, lat])
    const results: FireFeatureHit[] = []

    for (const def of geojsonDefs) {
      const cached = this.dataCache.get(def.type)
      if (!cached) continue

      const isPolygonType = POLYGON_TYPES.has(def.type)

      for (const feature of cached.data.features) {
        if (def.type === 'wfigs-fires') {
          const it = feature.properties?.incidentType as string | undefined
          if (it && !this._wfigsIncidentTypes.has(it as WfigsIncidentType)) continue
        }
        if (def.type === 'firms-hotspots') {
          const p = feature.properties
          if (p) {
            const sensor = p.sensor as string
            const conf = CONFIDENCE_RAW_MAP[p.confidence as string]
            if (!this._firmsSensors.has(sensor as FirmsSensor)) continue
            if (conf && !this._firmsConfidence.has(conf)) continue
          }
        }

        const geomType = feature.geometry.type
        const isPolygonFeature =
          isPolygonType || geomType === 'Polygon' || geomType === 'MultiPolygon'

        if (isPolygonFeature) {
          try {
            if (
              booleanPointInPolygon(
                pt,
                feature as GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon>
              )
            ) {
              results.push({
                fireType: def.type,
                feature,
                properties: (feature.properties ?? {}) as Record<string, unknown>,
                distanceKm: 0,
              })
            }
          } catch {
            // malformed geometry
          }
        } else {
          const centroid = featureCentroid(feature)
          if (centroid) {
            const dist = haversineKm(lat, lng, centroid[1], centroid[0])
            if (dist <= radiusKm) {
              results.push({
                fireType: def.type,
                feature,
                properties: (feature.properties ?? {}) as Record<string, unknown>,
                distanceKm: dist,
              })
            }
          }
        }
      }
    }

    // Best-effort PMTiles query via map source
    const pmtilesDef = LAYER_DEFS.find((d): d is PMTilesLayerDef => d.kind === 'pmtiles')
    if (pmtilesDef && this.map?.getSource(pmtilesDef.sourceId)) {
      try {
        const source = this.map.getSource<maplibregl.VectorTileSource>(pmtilesDef.sourceId)
        if (source) {
          const features = this.map.querySourceFeatures(pmtilesDef.sourceId, {
            sourceLayer: pmtilesDef.sourceLayer,
          })
          for (const feature of features) {
            if (feature.geometry.type === 'Polygon' || feature.geometry.type === 'MultiPolygon') {
              try {
                if (
                  booleanPointInPolygon(
                    pt,
                    feature as unknown as GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon>
                  )
                ) {
                  results.push({
                    fireType: pmtilesDef.type,
                    feature: feature as unknown as GeoJSON.Feature,
                    properties: (feature.properties ?? {}) as Record<string, unknown>,
                    distanceKm: 0,
                  })
                }
              } catch {
                // malformed geometry
              }
            }
          }
        }
      } catch {
        // PMTiles query not available
      }
    }

    return results
  }

  private async ensureDataLoaded(def: GeoJSONLayerDef): Promise<void> {
    const cached = this.dataCache.get(def.type)
    if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) return

    try {
      const url = `${BASE_URL}/${def.file}`
      const resp = await fetch(url)
      if (!resp.ok) {
        log.warn(`Failed to fetch ${url}: ${resp.status}`)
        return
      }
      const data: GeoJSON.FeatureCollection = await resp.json()
      this.dataCache.set(def.type, { data, fetchedAt: Date.now() })
      log.info(`Loaded ${data.features.length} ${def.type} features`)
    } catch (err) {
      log.warn(`Failed to load ${def.type} data`, err)
    }
  }

  private async loadData(def: GeoJSONLayerDef): Promise<void> {
    await this.ensureDataLoaded(def)
    const cached = this.dataCache.get(def.type)
    if (cached) this.setSourceData(def, cached.data)
  }

  private setSourceData(def: GeoJSONLayerDef, data: GeoJSON.FeatureCollection): void {
    if (!this.map || !this.enabledTypes.has(def.type)) return
    const source = this.map.getSource<maplibregl.GeoJSONSource>(def.sourceId)
    if (source) source.setData(data)
  }
}

export const fireLayerService = new FireLayerService()
